<template>
  <v-row class="ma-0">
    <v-col cols="2" class="pa-0">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-if="
              params &&
              params.context &&
              params.context.parentComponent &&
              params.context.parentComponent.permissions &&
              params.context.parentComponent.permissions.change
            "
            v-on="on"
            v-bind="attrs"
            class="mx-2 primary--text"
            @click="openDetails"
          >
            mdi-eye
          </v-icon>
        </template>
        <span>Details</span>
      </v-tooltip>

      <v-dialog v-model="detailsModalOpen" width="1100" scrollable>
        <v-card>
          <v-card-title class="details-header d-flex align-center">
            <span>Details</span>
            <v-spacer />
            <v-icon color="#fff" @click="detailsModalOpen = false"
              >mdi-close</v-icon
            >
          </v-card-title>

          <v-card-text class="pa-0">
            <div class="details-subheader d-flex align-center px-4 py-2">
              <span v-if="detailTitle" class="text-body-1">{{ detailTitle }}</span>
              <span v-if="historyList.length" class="reference-count-chip px-2 py-1">
                {{ historyList.length }} {{ historyList.length === 1 ? "attempt" : "attempts" }}
              </span>
            </div>

            <div class="px-4 py-3" v-if="referenceInfo">
              <v-simple-table dense class="history-table">
                <template #default>
                  <thead>
                    <tr>
                      <th>Request Id</th>
                      <th>Action On</th>
                      <th>Method</th>
                      <th>Detail</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="attempt in historyList" :key="attempt.id">
                      <td>{{ attempt.request_id || "N/A" }}</td>
                      <td>{{ formatDate(attempt.created) }}</td>
                      <td>{{ attempt.method || "N/A" }}</td>
                      <td>{{ attempt.detail || "N/A" }}</td>
                      <td>
                        <span
                          :class="attemptIsSuccess(attempt) ? 'success-border px-2 py-1 success--text' : 'error-border px-2 py-1 error--text'"
                        >
                          {{ attemptIsSuccess(attempt) ? "Success" : "Failed" }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!historyLoading && !historyList.length">
                      <td colspan="5" class="text-center grey--text pa-4">
                        No history found.
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <div class="px-4 py-3">
              <v-row class="mt-2">
                <v-col cols="6">
                  <div class="body-box d-flex align-center justify-space-between px-3 py-2">
                    <strong>Request Body</strong>
                    <v-icon small @click="copyToClipboard(requestBodyText)"
                      >mdi-content-copy</v-icon
                    >
                  </div>
                  <div class="body-content pa-3">
                    <vue-json-pretty :data="params.data.request_body" />
                  </div>
                </v-col>

                <v-col cols="6">
                  <div class="body-box d-flex align-center justify-space-between px-3 py-2">
                    <strong>Response Body</strong>
                    <v-icon small @click="copyToClipboard(responseBodyText)"
                      >mdi-content-copy</v-icon
                    >
                  </div>
                  <div class="body-content pa-3">
                    <vue-json-pretty :data="params.data.response_body" />
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="detailsModalOpen = false"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>

    <v-col cols="2" class="ma-0 pa-0 pl-3">
      <v-tooltip top>
        <template #activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs">
            <v-icon
              class="mx-2 primary--text"
              @click="!isResendDisabled && resendFailedData(params.data)"
              v-if="shouldShowResendButton && !isSuccess"
              :disabled="isResendDisabled"
            >
              mdi-send
            </v-icon>
          </span>
        </template>
        <span>{{ isResendDisabled ? "Already sent successfully" : "Resend" }}</span>
      </v-tooltip>
    </v-col>

    <v-col cols="2" class="ma-0 pa-0 pl-3">
      <v-tooltip top>
        <template #activator="{ on, attrs }">
          <v-icon
            class="mx-2 primary--text"
            v-on="on"
            v-bind="attrs"
            @click="openFailedRecords"
            v-if="shouldShowFailedButton"
          >
            mdi-pencil
          </v-icon>
        </template>
        <span>Failed Records</span>
      </v-tooltip>
    </v-col>

  </v-row>
</template>

<script>
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

export default {
  components: {
    VueJsonPretty,
  },
  data() {
    return {
      detailsModalOpen: false,
      historyList: [],
      historyLoading: false,
    };
  },
  computed: {
    shouldShowResendButton() {
      const parent = this.params?.context?.parentComponent;
      const isSuperuser = parent?.currentProfile?.is_superuser;
      const hasPermission = parent?.permissions?.change;
      const canResend = this.params?.data?.resend;

      if (!hasPermission) return false;
      if (typeof parent?.resendSAPData !== "function") return false;

      return isSuperuser || canResend;
    },

    isResendDisabled() {
      return this.params?.data?.resend === false;
    },

    shouldShowFailedButton() {
      const parent = this.params?.context?.parentComponent;
      return parent?.tab === 4 && (this.params?.data?.rejected > 0);
    },

    isSuccess() {
      const data = this.params?.data || {};
      if (typeof data.is_success === "boolean") return data.is_success;
      if (typeof data.is_success === "string")
        return data.is_success.toLowerCase() === "yes";
      return /^2/.test((data.response_code || "").toString());
    },

    detailTitle() {
      const data = this.params?.data || {};
      const idField =
        data.invoice_no ||
        data.receipt_id ||
        data.sub_hire_id ||
        data.credit_note_no ||
        data.debit_note_no ||
        data.sap_payout_id ||
        data.ref_id ||
        data.integration_id ||
        data.vbs_reference_number;
      if (idField) return `Reference | ${idField}`;
      return data.detail ? `Reference | ${data.detail}` : "";
    },

    formattedActionOn() {
      const parent = this.params?.context?.parentComponent;
      const value = this.params?.data?.created;
      if (!value) return "N/A";
      return parent?.$globals?.formatDate
        ? parent.$globals.formatDate(value, true)
        : value;
    },

    referenceInfo() {
      const data = this.params?.data || {};
      const map = [
        { field: "invoice_no", method: "getCustomerInvoiceIntegrationLogs" },
        { field: "receipt_id", method: "getPaymentReceiptIntegrationLogs" },
        { field: "sub_hire_id", method: "getSubHireIntegrationLogs" },
        { field: "credit_note_no", method: "getCreditNoteIntegrationLogs" },
        { field: "debit_note_no", method: "getDebitNoteIntegrationLogs" },
        { field: "sap_payout_id", method: "getSapIntegrationLogs" },
      ];
      const match = map.find((m) => data[m.field]);
      if (!match) return null;
      return { field: match.field, value: data[match.field], method: match.method };
    },

    requestBodyText() {
      return JSON.stringify(this.params?.data?.request_body, null, 2);
    },

    responseBodyText() {
      return JSON.stringify(this.params?.data?.response_body, null, 2);
    },
  },

  methods: {
    resendFailedData(sapData) {
      this.params.context.parentComponent.resendSAPData(sapData);
    },

    openFailedRecords() {
      this.params.context.parentComponent.openFailedRecordsDialog(this.params.data);
    },

    attemptIsSuccess(attempt) {
      if (typeof attempt.is_success === "boolean") return attempt.is_success;
      if (typeof attempt.is_success === "string")
        return attempt.is_success.toLowerCase() === "yes";
      return /^2/.test((attempt.response_code || "").toString());
    },

    formatDate(value) {
      const parent = this.params?.context?.parentComponent;
      if (!value) return "N/A";
      return parent?.$globals?.formatDate
        ? parent.$globals.formatDate(value, true)
        : value;
    },

    openDetails() {
      this.detailsModalOpen = true;
      this.historyList = [this.params.data];
      this.fetchHistory();
    },

    fetchHistory() {
      const info = this.referenceInfo;
      if (!info || !this.$api?.integration?.[info.method]) return;

      this.historyLoading = true;
      this.$api.integration[info.method]({ [info.field]: info.value })
        .then((res) => {
          this.historyList = res.data || [];
        })
        .catch(() => {
          this.historyList = [this.params.data];
        })
        .finally(() => {
          this.historyLoading = false;
        });
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text || "");
      this.$bus.$emit("showToastMessage", {
        message: "Copied to clipboard",
        color: "success",
      });
    },
  },
};
</script>

<style>
.DynamicDialog {
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: justify;
}

.details-header {
  background-color: #f07025;
  color: #fff;
}

.details-subheader {
  background-color: #eef7ee;
}

.body-box {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-bottom: none;
}

.body-content {
  max-height: 450px;
  overflow: auto;
  border: 1px solid #e0e0e0;
  margin: 0;
  background-color: #fff;
}

.history-table {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.history-table table {
  border-collapse: separate;
}

.history-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #fafafa !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #555 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  white-space: nowrap;
}

.history-table tbody td {
  font-size: 13px;
  white-space: nowrap;
}

.history-table tbody tr:hover {
  background-color: #fafafa;
}

.reference-count-chip {
  background-color: #eef1f6;
  color: #555;
  border-radius: 30px;
  font-size: 12px;
  margin-left: 8px;
}
</style>
